import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon, Modal } from 'semantic-ui-react';

import { addModifiers } from '../../shared/helpers';
import { getIsCustomPreferencesFetching } from '../../reducers/CustomPreferencesReducer/CustomPreferencesSelectors';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { LANGUAGE_OPTIONS, useI18nContext } from '../../shared/services/i18n';
import { useChangeLanguage } from '../../shared/hooks/useChangeLanguage';
import './PreferencesModal.scss';

interface PreferencesModalProps {
  open: boolean;
  onClose: () => void;
}

export const PreferencesModal = ({ open, onClose }: PreferencesModalProps) => {
  const i18nContext = useI18nContext();
  const changeLanguage = useChangeLanguage();
  const isFetching = useSelector(getIsCustomPreferencesFetching);

  const [selectedLanguage, setSelectedLanguage] = useState(i18nContext.language);

  // The modal stays mounted while logged in, so state persists across open/close cycles.
  // Reset the selection to the active language whenever the modal opens so a dismissed
  // (unsaved) choice doesn't linger and show a stale checkmark on reopen.
  useEffect(() => {
    if (open) {
      setSelectedLanguage(i18nContext.language);
    }
  }, [open, i18nContext.language]);

  const handleSave = async () => {
    if (selectedLanguage !== i18nContext.language) {
      await changeLanguage(selectedLanguage);
    } else {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeIcon size="tiny" className="preferences-modal">
      <Modal.Header className="preferences-modal__header">
        <FormattedMessageHelper id="language.modal.title" />
      </Modal.Header>
      <Modal.Content className="preferences-modal__content">
        <div className="preferences-modal__language-note">
          <Icon name="info circle" className="preferences-modal__language-note-icon" />
          <span className="preferences-modal__language-note-text">
            <FormattedMessageHelper id="language.modal.languageNote" />
          </span>
        </div>
        <ul className="preferences-modal__options">
          {LANGUAGE_OPTIONS.map(option => (
            <li
              key={option.value}
              className={addModifiers('preferences-modal__option', option.value === selectedLanguage ? ['selected'] : [])}
              onClick={() => setSelectedLanguage(option.value)}
            >
              <span className="preferences-modal__option-label">{option.label}</span>
              {option.value === selectedLanguage && <Icon name="check" className="preferences-modal__option-check" />}
            </li>
          ))}
        </ul>
      </Modal.Content>
      <Modal.Actions className="preferences-modal__actions">
        <button className="button button--primary preferences-modal__save" onClick={handleSave} disabled={isFetching}>
          <FormattedMessageHelper id="language.modal.save" />
        </button>
      </Modal.Actions>
    </Modal>
  );
};

export default PreferencesModal;
